/**
 * Laufzeit der EIGENBEWEGUNG (Rechnung: post-types/eigenbewegung.ts).
 *
 * - Uhr je Ebene: startet, sobald die Ebene sichtbar wird (Scroll-Zeit im
 *   Fenster from..to), und beginnt beim Verlassen neu.
 * - Angehalten fuer Ebenen, die der Pro-Editor gerade bearbeitet: sonst
 *   dreht sich die ausgewaehlte Ebene unter dem Anfasser weg und ein
 *   Anfasser-Dreh wuerde den Schleifen-Winkel als Keyframe speichern.
 * - „Bewegung reduzieren" am Geraet: keine Eigenbewegung.
 *
 * Kein eigener Zeichen-Takt noetig: der Renderer zeichnet ohnehin jedes Bild
 * (SlidePlayer-Loop), auch ohne Scrollen.
 */
import type { LayerEigenbewegung } from "@scrolly/post-types";
import { wendeEigenbewegungAn } from "@scrolly/post-types";

const angehalten = new Set<string>();
/** Bei angehaltenen Ebenen trotzdem laufen lassen: nur dieser Kanal („Bewegung ansehen"). */
let angesehenerKanal: keyof LayerEigenbewegung | null = null;

/**
 * Pro-Editor: diese Ebenen stehen still (die ausgewaehlten). `nurKanal`:
 * „Bewegung ansehen" — bei ihnen laeuft dann genau dieser eine Kanal.
 */
export function setzeEigenbewegungAngehalten(
    ids: Iterable<string>,
    nurKanal: keyof LayerEigenbewegung | null = null,
) {
    angehalten.clear();
    for (const id of ids) angehalten.add(id);
    angesehenerKanal = nurKanal;
}

/**
 * KACHEL-AUFNAHME (Jans Fund 02.10.2026, Postkarten-Template): Solange eine
 * Aufnahme an einem FREMDEN Timer laeuft, liefert jede Ebene ihre Ruhepose.
 *
 * Die Kachel-Aufnahmen des Template-Editors (captureFrame.ts) rendern die
 * Buehne kurz an einer anderen Stelle. Mit Eigenbewegung zeigte jede Aufnahme
 * eine ANDERE Schwenk-Stellung — die Bytes unterschieden sich, die
 * Dubletten-Pruefung griff nie, und der Editor tauschte das Kachelbild nach
 * jeder Aenderung dreimal aus (gemessen: Wechsel bei 1,8 / 4,6 / 8,5 s),
 * samt Neuzeichnen und Hochladen. Auf der laufenden Buehne war das als
 * kurzes Zucken zu sehen.
 *
 * Die Uhr wird dabei NICHT angefasst: nach der Aufnahme laeuft die Bewegung
 * genau dort weiter, wo sie war.
 */
let aufnahmeRuhepose = false;
export function setzeEigenbewegungAufnahme(an: boolean) {
    aufnahmeRuhepose = an;
}

let wenigerBewegung = false;
if (typeof window !== "undefined" && typeof window.matchMedia === "function") {
    const mq = window.matchMedia("(prefers-reduced-motion: reduce)");
    wenigerBewegung = mq.matches;
    mq.addEventListener?.("change", (e) => {
        wenigerBewegung = e.matches;
    });
}

/**
 * EXPORT-UHR (Video-Export in „My Scrollys", Jan 16.10.2026): Das Video wird
 * Bild für Bild gerechnet, schneller oder langsamer als Echtzeit. Damit
 * Drehen/Atmen/Schweben im Video im richtigen Tempo laufen, liefert der
 * Export hier seine VIDEO-Zeit (ms). null = wieder die echte Uhr.
 */
let exportUhr: (() => number) | null = null;
/** Date.now() zum Video-Zeitpunkt 0 (s. videoAuswahlJetzt) */
let exportBasis = 0;
/**
 * performance.now() zum Video-Zeitpunkt 0. Die Video-Uhr setzt NAHTLOS an die
 * echte Uhr an (Jans Fund 16.10.2026: der drehende Schuh stand im Video
 * still). Der Export besucht vorab jede Slide auf der echten Uhr — dabei
 * merkt sich die Eigenbewegung „sichtbar seit" = performance.now() (groß).
 * Lief das Video danach ab 0, war „vergangen" immer negativ, und
 * wendeEigenbewegungAn klemmt negative Zeit auf 0: keine Drehung.
 */
let exportBasisPerf = 0;
export function setzeEigenbewegungUhr(uhr: (() => number) | null) {
    if (uhr && !exportUhr) {
        exportBasis = Date.now() - uhr();
        exportBasisPerf = (typeof performance !== "undefined" ? performance.now() : Date.now()) - uhr();
    }
    exportUhr = uhr;
}

/**
 * Uhr der VIDEO-AUSWAHL (welches Video spielt, RendererWebGL): ihre
 * Frische-Fenster (250 ms) messen sonst Wanduhr-Zeit — im Export dauert ein
 * Bild in Safari aber länger als das, alle Einträge galten als veraltet und
 * das aktive Video sprang von Bild zu Bild (Messung 16.10.2026, Jans
 * Videos-in-voller-Länge). Im Export läuft sie deshalb auf der Video-Uhr,
 * anschließend an die echte Zeit (ältere Einträge altern normal).
 */
export function videoAuswahlJetzt(): number {
    return exportUhr ? exportBasis + exportUhr() : Date.now();
}

const jetzt = () =>
    exportUhr
        ? exportBasisPerf + exportUhr()
        : typeof performance !== "undefined"
          ? performance.now()
          : Date.now();

/**
 * Umhuellt `layer3D(t)` einer Ebene. Ohne Eigenbewegung wird die Funktion
 * unveraendert zurueckgegeben — kein Mehraufwand fuer alle anderen Ebenen.
 */
export function mitEigenbewegung<
    T extends {
        rotation?: number[] | null;
        opacity?: number | null;
        position?: number[] | null;
        scale?: number[] | null;
    },
>(
    ebene: { id: string; from: number; to: number; loops?: LayerEigenbewegung },
    basis: (t: number) => T,
): (t: number) => T {
    const eb = ebene.loops;
    if (!eb || !(eb.rotation?.active || eb.opacity?.active || eb.position?.active || eb.scale?.active)) {
        return basis;
    }
    let sichtbarSeit: number | null = null;
    return (t: number) => {
        const werte = basis(t);
        // Kachel-Aufnahme: Ruhepose, Uhr unberuehrt (s. setzeEigenbewegungAufnahme)
        if (aufnahmeRuhepose) return werte;
        const sichtbar = t >= ebene.from && t <= ebene.to;
        if (!sichtbar) {
            sichtbarSeit = null;
            return werte;
        }
        if (sichtbarSeit === null) sichtbarSeit = jetzt();
        if (wenigerBewegung) return werte;
        if (angehalten.has(ebene.id)) {
            if (!angesehenerKanal) return werte;
            return wendeEigenbewegungAn(werte, { [angesehenerKanal]: eb[angesehenerKanal] }, jetzt() - sichtbarSeit);
        }
        return wendeEigenbewegungAn(werte, eb, jetzt() - sichtbarSeit);
    };
}
